// Page Profil — Story 18
// Affiche les infos SSO PreventionBTP en lecture seule + SIRET modifiable localement
// pour l'entreprise actuellement sélectionnée. Le SIRET local ne remonte pas au SSO.

import { useState, useEffect } from 'react'
import { ExternalLink, User as UserIcon, Building2, Mail, Briefcase, Pencil, Check, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useEntreprise } from '../contexts/EntrepriseContext'
import { authApi } from '../services/api'
import { Button } from '../components/ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui'
import { Badge } from '../components/ui/badge'
import { Separator } from '../components/ui/separator'

const PREVENTIONBTP_URL = 'https://www.preventionbtp.fr'

export default function ProfilePage() {
  const { user, refreshAuth } = useAuth()
  const { currentCompany, currentSiret, currentSiretSource } = useEntreprise()
  const [editing, setEditing] = useState(false)
  const [siretValue, setSiretValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Resync l'input à chaque changement d'org courante (le SIRET est par org)
  useEffect(() => {
    setSiretValue(currentSiret ?? '')
    setEditing(false)
    setError(null)
  }, [currentCompany?.id, currentSiret])

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-3 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  const handleSave = async () => {
    setError(null)
    if (!currentCompany?.id) {
      setError("Aucune organisation sélectionnée.")
      return
    }
    const trimmed = siretValue.trim()
    if (trimmed.length === 0) {
      // Effacer le SIRET local pour cette org
      setSaving(true)
      try {
        await authApi.updateSiret(currentCompany.id, null)
        await refreshAuth()
        setEditing(false)
      } catch (e: any) {
        setError(e?.message || "Erreur lors de l'enregistrement")
      } finally {
        setSaving(false)
      }
      return
    }
    if (!/^\d{14}$/.test(trimmed)) {
      setError('Le SIRET doit contenir exactement 14 chiffres.')
      return
    }
    setSaving(true)
    try {
      await authApi.updateSiret(currentCompany.id, trimmed)
      await refreshAuth()
      setEditing(false)
    } catch (e: any) {
      setError(e?.message || "Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setSiretValue(currentSiret ?? '')
    setError(null)
    setEditing(false)
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Mon profil</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Vos informations personnelles et entreprise.
        </p>
      </div>

      {/* Infos SSO — lecture seule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserIcon className="size-4 text-primary" />
            Identifiants PréventionBTP
          </CardTitle>
          <CardDescription className="text-xs">
            Ces informations proviennent de votre compte PréventionBTP. Pour les modifier,
            rendez-vous sur{' '}
            <a
              href={PREVENTIONBTP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              preventionbtp.fr <ExternalLink className="size-3" />
            </a>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ReadOnlyField icon={<UserIcon className="size-4" />} label="Nom complet" value={`${user.prenom} ${user.nom}`.trim() || '—'} />
          <Separator />
          <ReadOnlyField icon={<Mail className="size-4" />} label="Email" value={user.email || '—'} />
          {user.position && (
            <>
              <Separator />
              <ReadOnlyField icon={<Briefcase className="size-4" />} label="Fonction" value={user.position} />
            </>
          )}
          {currentCompany && (
            <>
              <Separator />
              <ReadOnlyField
                icon={<Building2 className="size-4" />}
                label="Entreprise"
                value={currentCompany.name}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* SIRET — modifiable localement, par entreprise courante */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="size-4 text-primary" />
            Numéro SIRET {currentCompany && <span className="text-muted-foreground font-normal">— {currentCompany.name}</span>}
          </CardTitle>
          <CardDescription className="text-xs">
            {currentSiretSource === 'sso'
              ? 'Le SIRET est synchronisé depuis PréventionBTP. Pour le modifier, rendez-vous sur preventionbtp.fr.'
              : 'Le SIRET est utilisé uniquement par cet outil. Il ne sera pas remonté à PréventionBTP.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentSiretSource === 'sso' ? (
            // SIRET fourni par le SSO — affichage seul
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-base text-foreground">{currentSiret}</span>
              <Badge variant="outline" className="text-xs">Synchronisé PréventionBTP</Badge>
            </div>
          ) : !editing ? (
            // SIRET local : affichage avec bouton "Modifier"
            <div className="flex items-center justify-between gap-3">
              <div>
                {currentSiret ? (
                  <span className="font-mono text-base text-foreground">{currentSiret}</span>
                ) : (
                  <span className="text-sm text-muted-foreground italic">Non renseigné</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-1.5">
                <Pencil className="size-3.5" />
                {currentSiret ? 'Modifier' : 'Renseigner'}
              </Button>
            </div>
          ) : (
            // Mode édition
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="siret-input">
                  SIRET (14 chiffres)
                </label>
                <input
                  id="siret-input"
                  type="text"
                  inputMode="numeric"
                  value={siretValue}
                  onChange={(e) => {
                    setSiretValue(e.target.value.replace(/\D/g, '').slice(0, 14))
                    setError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave()
                    if (e.key === 'Escape') handleCancel()
                  }}
                  placeholder="12345678901234"
                  autoFocus
                  className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-primary/30"
                />
                {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Laissez vide pour effacer. {siretValue.length}/14
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving} className="gap-1.5">
                  <X className="size-3.5" />
                  Annuler
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                  <Check className="size-3.5" />
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ReadOnlyField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 text-muted-foreground min-w-0">
        <span className="flex-shrink-0">{icon}</span>
        <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
      </div>
      <span className="text-sm font-medium text-foreground truncate" title={value}>{value}</span>
    </div>
  )
}
